/**
 * Состояние тренировочной сессии.
 *
 * Класс держит только то, что нужно для мгновенной обратной связи: текущую
 * позицию, счётчики и накопленные события. Сеть здесь не участвует (AD-003).
 */

import type {
  ErrorKind,
  KeystrokeEvent,
  LayoutId,
  ModifierState,
  OsPlatform,
  SessionId,
  TypingError,
  TypingMetrics,
} from '@type2x/shared-contracts';
import { NO_MODIFIERS } from '@type2x/shared-contracts';
import { getLayout, type KeyboardLayout } from '@type2x/keyboard-layouts';

import { classifyKeystroke } from './classify.js';
import { computeMetrics, type MetricCounters } from './metrics.js';

/** Результат обработки одного нажатия. */
export interface KeystrokeOutcome {
  readonly correct: boolean;
  readonly errorKind: ErrorKind | null;
  /** Позиция, к которой перешёл курсор после нажатия. */
  readonly position: number;
  /** Завершён ли целевой текст. */
  readonly finished: boolean;
}

/** Ввод, поступивший от адаптера клавиатуры. */
export interface KeystrokeInput {
  readonly character: string;
  /** Монотонная метка времени, мс. */
  readonly monotonicMs: number;
  readonly modifiers?: ModifierState;
}

/** Параметры создания сессии. */
export interface SessionOptions {
  readonly sessionId: SessionId;
  readonly layoutId: LayoutId;
  /**
   * Операционная система пользователя.
   *
   * От неё зависит расположение части клавиш (ТЗ FR-EN-42), а значит и разбор
   * ошибок: промах по соседней клавише на macOS и на Windows — это промахи по
   * разным клавишам.
   */
  readonly platform?: OsPlatform;
  readonly targetText: string;
  /** Разрешено ли исправление ошибок Backspace. */
  readonly allowCorrections?: boolean;
}

/** Текущее состояние набора для отрисовки. */
export interface SessionState {
  readonly position: number;
  readonly targetText: string;
  readonly typedText: string;
  readonly metrics: TypingMetrics;
  readonly finished: boolean;
}

/**
 * Сессия набора одного текста.
 *
 * Ошибка не блокирует движение вперёд: курсор идёт дальше, а сам факт ошибки
 * фиксируется. Это соответствует правилу «ошибка исправляется сразу, но без
 * наказаний» (ТЗ §7.7).
 */
export class TypingSession {
  private readonly layout: KeyboardLayout;
  private readonly target: readonly string[];
  private readonly allowCorrections: boolean;
  private readonly events: KeystrokeEvent[] = [];
  private readonly errors: TypingError[] = [];
  private readonly intervals: number[] = [];
  private readonly typed: string[] = [];

  private cursor = 0;
  private correctCount = 0;
  private correctionCount = 0;
  /**
   * Нажатия за всю сессию, включая стёртые.
   *
   * Отдельно от длины набранного: Backspace укорачивает текст, но не отменяет
   * того, что нажатие было сделано.
   */
  private keystrokeCount = 0;
  private firstTryCorrectCount = 0;
  private startedAtMonotonic: number | null = null;
  private lastMonotonic: number | null = null;

  constructor(private readonly options: SessionOptions) {
    this.layout = getLayout(options.layoutId, options.platform);
    this.target = [...options.targetText];
    this.allowCorrections = options.allowCorrections ?? true;
  }

  /** Ожидаемый символ в текущей позиции; пустая строка после конца текста. */
  get expectedCharacter(): string {
    return this.target[this.cursor] ?? '';
  }

  /** Завершён ли текст. */
  get finished(): boolean {
    return this.cursor >= this.target.length;
  }

  /** Все зафиксированные события нажатий. Остаются на клиенте. */
  get keystrokeEvents(): readonly KeystrokeEvent[] {
    return this.events;
  }

  /** Все зафиксированные ошибки. */
  get typingErrors(): readonly TypingError[] {
    return this.errors;
  }

  /** Межклавишные интервалы, мс. */
  get interKeyIntervals(): readonly number[] {
    return this.intervals;
  }

  /** Текущие метрики набора. */
  get metrics(): TypingMetrics {
    return computeMetrics(this.counters());
  }

  /** Состояние для отрисовки. */
  get state(): SessionState {
    return {
      position: this.cursor,
      targetText: this.options.targetText,
      typedText: this.typed.join(''),
      metrics: this.metrics,
      finished: this.finished,
    };
  }

  private counters(): MetricCounters {
    const elapsedMs =
      this.startedAtMonotonic === null || this.lastMonotonic === null
        ? 0
        : this.lastMonotonic - this.startedAtMonotonic;

    return {
      typedCharacters: this.typed.length,
      correctCharacters: this.correctCount,
      corrections: this.correctionCount,
      keystrokes: this.keystrokeCount,
      firstTryCorrect: this.firstTryCorrectCount,
      elapsedMs,
      intervals: this.intervals,
    };
  }

  /**
   * Обрабатывает одно нажатие.
   *
   * @returns результат для мгновенной обратной связи.
   */
  applyKeystroke(input: KeystrokeInput): KeystrokeOutcome {
    if (this.finished) {
      return { correct: false, errorKind: null, position: this.cursor, finished: true };
    }

    const expected = this.expectedCharacter;
    const interval = this.lastMonotonic === null ? 0 : input.monotonicMs - this.lastMonotonic;

    this.startedAtMonotonic ??= input.monotonicMs;
    this.lastMonotonic = input.monotonicMs;
    if (interval > 0) this.intervals.push(interval);

    const errorKind = classifyKeystroke(this.layout, expected, input.character);
    const correct = errorKind === null;

    this.events.push({
      expectedCharacter: expected,
      actualCharacter: input.character,
      occurredAtMonotonic: input.monotonicMs,
      interKeyIntervalMs: interval,
      position: this.cursor,
      corrected: false,
      modifierState: input.modifiers ?? NO_MODIFIERS,
      layoutId: this.options.layoutId,
      sessionId: this.options.sessionId,
    });

    this.keystrokeCount += 1;
    if (correct) {
      this.correctCount += 1;
      this.firstTryCorrectCount += 1;
    } else {
      this.errors.push({
        kind: errorKind,
        expected,
        actual: input.character,
        position: this.cursor,
        hesitationMs: interval,
      });
    }

    this.typed.push(input.character);
    this.cursor += 1;

    return { correct, errorKind, position: this.cursor, finished: this.finished };
  }

  /**
   * Откатывает последний введённый символ.
   *
   * @returns true, если откат выполнен. В контрольном режиме исправления
   *   запрещены и метод всегда возвращает false.
   */
  backspace(): boolean {
    if (!this.allowCorrections || this.cursor === 0) return false;

    const removed = this.typed.pop();
    this.cursor -= 1;
    this.correctionCount += 1;

    const wasCorrect = removed === this.target[this.cursor];
    if (wasCorrect) this.correctCount -= 1;

    return true;
  }
}
